import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  getAllRequests, setRequest, withdrawRequest, deleteRequest, getCurrentuser, deactivateRequest, hideRequests, claimRequest, assignSupervisor, approveRequest, rejectRequest, assignStaffs, getActivators, getAssigneeEmployee
} from "../../store/actions";
import {
  Button,
  Table,
  Modal,
  Breadcrumb,
  useToast
} from "../UI";
import "./request.css";
import {
  PlusCircle,
  Edit2,
  Trash2,
  Search as SearchIcon,
  AlertCircle,
  MinusCircle,
  ClipboardList,
  CheckCircle2, Clock3, XCircle
} from "lucide-react";
import { useTableSearch } from "../Common/useTableSearch";
import axios from "axios";
import RequestDetails from "./RequestDetails";
import { getUserRole } from "../../utils/roles";

const RequestList = ({
  getAllRequests,
  setRequest,
  singleRequst,
  deleteRequest,
  deactivateRequest,
  getCurrentuser,
  user,
  hideRequests,
  claimRequest,
  getAssigneeEmployee,
  getActivators,
  approveRequest,
  rejectRequest,
  assignSupervisor,
  assignStaffs
}: any) => {
  const { toast } = useToast();
  const [refetchKey, setRefetchKey] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);
  const [searchVal, setSearchVal] = useState<any>("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [targetRequestId, setTargetRequestId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'processed' | 'archived'>('active');

  const handleRefresh = () => setRefetchKey(prev => prev + 1);

  const fetchUsers = useCallback(async () => {
    if (!user?.oracleId) return { data: [] };
    try {
      const isArchived = activeTab === 'archived';
      const { data } = await axios.get(`api/requests/getallrequest/${user.oracleId}?showHidden=${isArchived}`);

      let filtered = data;
      const userRoles = user?.roles || (user?.role ? [user.role] : []);
      const isCardActivator =
        user?.jobRole?.toLowerCase() === 'card activator' ||
        userRoles.some((r: any) =>
          r.name?.toLowerCase() === 'card activator' ||
          r.permissions?.activate_cards === "FULL" ||
          (Array.isArray(r.permissions) && r.permissions.includes('activate:requests'))
        );
      const hasRole = (role: string) =>
        user?.jobRole?.toLowerCase() === role.toLowerCase() ||
        userRoles.some((r: any) => r.name?.toLowerCase() === role.toLowerCase()) ||
        (Array.isArray(user?.customRoles) && user?.customRoles.some((r: string) => r.toLowerCase() === role.toLowerCase()));

      if (activeTab === 'active') {
        filtered = data.filter((r: any) => {
          const isOwnRequest = r.requestername === user.fullName || r.requesterId === user.oracleId || r.requester?._id === user.oracleId || r.requesterOracleId === user.oracleId;
          if (isOwnRequest) {
            if (['Approved', 'Activated'].includes(r.status)) return false;
            if (r.status === 'Rejected') return true;
            return true;
          }
          if (['Approved', 'Activated', 'Rejected'].includes(r.status)) return false;

          const isAssignedToUser = r.approver?._id === user._id || r.approverId === user.oracleId || r.approverId === user._id || r.approverId === user.employeeId;
          const isCurrentApprover = isAssignedToUser && (
            (r.approvalStage === "Electronic Security Manager" && hasRole("Electronic Security Manager")) ||
            (r.approvalStage === "Security and Surveillance Supervisor" && (hasRole("Security and Surveillance Supervisor") || hasRole("Supervisor") || hasRole("Electronic Security Specialist"))) ||
            (r.approvalStage === "Card Activator" && isCardActivator) ||
            (r.approvalStage === "Officer" && user?.jobRole === "Officer") ||
            (r.approvalStage === "Manager" || r.approvalStage === "Executive" || r.approvalStage === "Excutive" || r.approvalStage === "Security Manager")
          );
          const isSecurityManagerPool = r.approvalStage === "Security Manager" && !r.approver && (user?.jobRole === "Security Manager" || user?.role?.name === "Security Manager");
          const isESMPool = r.approvalStage === "Electronic Security Manager" && hasRole("Electronic Security Manager");
          const isSupervisorPool = r.approvalStage === "Security and Surveillance Supervisor" && (!r.approver && !r.approverId) && (hasRole("Security and Surveillance Supervisor") || hasRole("Supervisor") || hasRole("Electronic Security Specialist"));
          const isActivatorPool = r.approvalStage === "Card Activator" && (!r.approver && !r.approverId) && isCardActivator;
          const isOfficerPool = r.approvalStage === "Officer" && user?.jobRole === "Officer" && (!r.approver || r.approverId === user.employeeId);
          const isCurrentlyResponsible = isCurrentApprover || isSecurityManagerPool || isESMPool || isSupervisorPool || isActivatorPool || isOfficerPool;

          if (isCurrentlyResponsible) return true;

          const hasProcessed = r.approvers?.some((app: any) => app.approverId === user.oracleId || app.approverId === user.employeeId || app.approverId === user._id || app.approverId === user.id);
          if (hasProcessed) return false;

          return false;
        });
      } else if (activeTab === 'processed') {
        filtered = data.filter((r: any) => {
          const isCompleted = ['Approved', 'Activated'].includes(r.status);
          const hasProcessed = r.approvers?.some((app: any) => app.approverId === user.oracleId || app.approverId === user.employeeId || app.approverId === user._id || app.approverId === user.id);

          const isOwnRequest = r.requestername === user.fullName || r.requesterId === user.oracleId || r.requester?._id === user.oracleId || r.requesterOracleId === user.oracleId;
          if (isOwnRequest) {
            return isCompleted;
          }

          const isAssignedToUser = r.approver?._id === user._id || r.approverId === user.oracleId || r.approverId === user._id || r.approverId === user.employeeId;
          const isCurrentApprover = isAssignedToUser && (
            (r.approvalStage === "Electronic Security Manager" && hasRole("Electronic Security Manager")) ||
            (r.approvalStage === "Security and Surveillance Supervisor" && (hasRole("Security and Surveillance Supervisor") || hasRole("Supervisor") || hasRole("Electronic Security Specialist"))) ||
            (r.approvalStage === "Card Activator" && isCardActivator) ||
            (r.approvalStage === "Officer" && user?.jobRole === "Officer") ||
            (r.approvalStage === "Manager" || r.approvalStage === "Executive" || r.approvalStage === "Excutive" || r.approvalStage === "Security Manager")
          );
          const isSecurityManagerPool = r.approvalStage === "Security Manager" && !r.approver && (user?.jobRole === "Security Manager" || user?.role?.name === "Security Manager");
          const isESMPool = r.approvalStage === "Electronic Security Manager" && hasRole("Electronic Security Manager");
          const isSupervisorPool = r.approvalStage === "Security and Surveillance Supervisor" && (!r.approver && !r.approverId) && (hasRole("Security and Surveillance Supervisor") || hasRole("Supervisor") || hasRole("Electronic Security Specialist"));
          const isActivatorPool = r.approvalStage === "Card Activator" && (!r.approver && !r.approverId) && isCardActivator;
          const isOfficerPool = r.approvalStage === "Officer" && user?.jobRole === "Officer" && (!r.approver || r.approverId === user.employeeId);
          const isCurrentlyResponsible = isCurrentApprover || isSecurityManagerPool || isESMPool || isSupervisorPool || isActivatorPool || isOfficerPool;

          if (isCurrentlyResponsible) return false;
          return hasProcessed;
        });
      }
      return { data: filtered };
    } catch (err) {
      console.error(err);
      return { data: [] };
    }
  }, [user?.oracleId, refetchKey, activeTab, user]);

  useEffect(() => {
    getAssigneeEmployee();
    getCurrentuser();
  }, [getAssigneeEmployee, getCurrentuser]);

  const handleHideSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      await hideRequests(selectedRowKeys, user.oracleId);
      toast(`${selectedRowKeys.length} items archived successfully`, 'success');
      setSelectedRowKeys([]);
      handleRefresh();
    } catch (err) {
      toast("Failed to archive requests", 'error');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      if (activeTab === 'archived') {
        await Promise.all(selectedRowKeys.map(id => deleteRequest(id)));
        toast(`${selectedRowKeys.length} items deleted permanently`, 'success');
      } else {
        await Promise.all(selectedRowKeys.map(id => withdrawRequest(id, user.oracleId)));
        toast(`${selectedRowKeys.length} items withdrawn successfully`, 'success');
      }
      setSelectedRowKeys([]);
      handleRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || (activeTab === 'archived' ? "Failed to delete requests" : "Failed to withdraw requests");
      toast(errMsg, 'error');
    }
  };

  const handleDelete = async () => {
    if (!targetRequestId) return;
    try {
      await deleteRequest(targetRequestId);
      toast("Request deleted permanently", 'success');
      setIsDeleteModalOpen(false);
      handleRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || "Failed to delete request";
      toast(errMsg, 'error');
    }
  };

  const handleDeactivate = async () => {
    if (!targetRequestId) return;
    try {
      await deactivateRequest(targetRequestId);
      toast("Card deactivated successfully", 'success');
      setIsDeactivateModalOpen(false);
      handleRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || "Deactivation failed";
      toast(errMsg, 'error');
    }
  };

  const { filteredData, loading: tableLoading } = useTableSearch({
    searchVal,
    retrieve: fetchUsers,
  });

  const columns = [
    {
      title: "Requester",
      key: "requestername",
      width: "18%",
      render: (val: any, record: any) => {
        const name = record.requestername || "Unknown";

        return (
          <div className="flex flex-col py-1 justify-center min-w-0">
            <span
              className="text-sm font-semibold text-slate-700 leading-tight block truncate"
              title={name}
            >
              {name}
            </span>
            <span className="text-[10px] text-slate-400 font-normal mt-0.5 tracking-normal block truncate">
              Id: {record.requester?.oracleId || record.requesterId || 'N/A'}
            </span>
          </div>
        );
      }
    },
    {
      title: "Allowed Sites",
      key: "allowedsite",
      width: "18%",
      render: (val: any, record: any) => {
        const siteNames = (record.allowedsite || []).map((site: any) => site.sitename).join(", ");
        return (
          <div
            className="py-1 text-sm text-slate-600 font-normal truncate whitespace-nowrap"
            title={siteNames}
          >
            {siteNames || <span className="text-slate-400">—</span>}
          </div>
        );
      }
    },
    {
      title: "Allowed Doors",
      key: "alloweddoor",
      width: "24%",
      render: (val: any, record: any) => {
        const doorNames = (record.alloweddoor || []).map((door: any) => door.alloweddoor).join(", ");
        return (
          <div
            className="py-1 text-sm text-slate-600 font-normal truncate whitespace-nowrap"
            title={doorNames}
          >
            {doorNames || <span className="text-slate-400">—</span>}
          </div>
        );
      }
    },
    {
      title: "Type",
      key: "requesttype",
      width: "12%",
      className: "text-left",
      onHeaderCell: () => ({ className: "text-left" }),
      render: (val: any) => (
        <div className="flex items-center text-left py-1 whitespace-nowrap text-slate-600 text-sm font-normal capitalize">
          {val}
        </div>
      )
    },
    {
      title: "Status",
      key: "status",
      width: "18%",
      className: "text-left pr-3",
      onHeaderCell: () => ({ className: "text-left pr-3" }),
      render: (val: any, record: any) => {
        const isRequester = record.requestername === user?.fullName ||
          record.requesterOracleId === user?.oracleId ||
          user?._id === (record.requester?._id || record.requesterId);

        const getApprovalStageSubtitle = (stage: string) => {
          switch (stage) {
            case "Manager": return "Awaiting Manager Approval";
            case "Officer": return "Awaiting Officer Approval";
            case "Electronic Security Manager": return "Awaiting Security Review";
            case "Card Activator": return "Pending Card Activation";
            default: return `Awaiting ${stage}`;
          }
        };

        const renderStatusHeader = () => {
          if (val === 'Approved' || val === 'Activated') {
            return (
              <div className="inline-flex items-center gap-1.5 font-semibold text-sm text-slate-800 whitespace-nowrap">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#8dc63f] shrink-0" />
                <span>Activated</span>
              </div>
            );
          }
          if (val === 'Pending') {
            return (
              <div className="inline-flex items-center gap-1.5 font-semibold text-xs text-slate-800 whitespace-nowrap">
                <Clock3 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span>Pending</span>
              </div>
            );
          }
          if (val === 'Rejected') {
            return (
              <div className="inline-flex items-center gap-1.5 font-semibold text-xs text-slate-800 whitespace-nowrap">
                <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <span>Rejected</span>
              </div>
            );
          }
          return null;
        };
        const subtitleText = val === 'Pending' && record.approvalStage ? getApprovalStageSubtitle(record.approvalStage) : "";

        return (
          <div className="flex flex-col items-start justify-center py-2 pr-2">
            {renderStatusHeader()}
            {subtitleText && (
              <span className="text-[10px] pt-1 text-slate-500 font-normal leading-tight whitespace-nowrap">
                {subtitleText}
              </span>
            )}
            {val === 'Rejected' && isRequester && (
              <span
                className="text-[9px] pt-1 text-rose-500 font-bold tracking-wider uppercase leading-none mt-0.5 animate-pulse whitespace-nowrap"
                title="Action Required: Your request was rejected"
              >
                Action Required
              </span>
            )}
          </div>
        );
      }
    },
    {
      title: "Action",
      key: "operation",
      width: "10%",
      className: "text-center",
      onHeaderCell: () => ({ className: "text-center" }),
      render: (_: any, record: any) => {
        const isRequester = user?._id === (record.requester?._id || record.requesterId);
        const isRejected = record.status === 'Rejected';
        const canEdit = isRequester && !record.approved && ((!record.approvers || record.approvers.length === 0) || isRejected);
        return (
          <div className="flex items-center justify-center gap-1.5 py-1">
            {canEdit && (
              <>
                <Link to="/editrequest" onClick={() => setRequest(record)} className={`p-1.5 rounded-md transition-colors ${isRejected ? 'text-rose-500 hover:bg-rose-50' : 'text-[#8dc63f]'}`} title={isRejected ? 'Edit & Resubmit' : 'Edit'}>
                  <Edit2 className="w-3.5 h-3.5" />
                </Link>
                {!isRejected && (
                  <button onClick={(e) => { e.stopPropagation(); setTargetRequestId(record._id); setIsDeleteModalOpen(true); }} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
            {/* Removed view (Eye) button; row click now opens details */}
            {isRequester && record.approved && !record.deactivated && (
              <button onClick={(e) => { e.stopPropagation(); setTargetRequestId(record._id); setIsDeactivateModalOpen(true); }} className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-md transition-colors">
                <MinusCircle className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      },
    }
  ];

  return (
    <div className="space-y-4">
      {/* Header & Breadcrumbs */}
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: getUserRole(user) === 'NORMAL_USER' ? '/requestlist' : '/dashboard' },
            { label: 'Request List' }
          ]}
        />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#8dc63f] rounded-xl flex items-center justify-center shadow-md shadow-[#8dc63f]/10">
              <ClipboardList className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Requests</h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Link to="/addrequest" className="flex-1 sm:flex-none">
              <Button className="w-full h-8.5 px-3 bg-[#8dc63f] hover:bg-[#76ab32] shadow-sm gap-1.5 text-xs font-semibold">
                <PlusCircle className="w-3.5 h-3.5" />
                Create Request
              </Button>
            </Link>
            {selectedRowKeys.length > 0 && (
              <Button
                variant="ghost"
                className={`h-8.5 px-3 ${activeTab === 'archived' ? 'text-rose-600 border-rose-100 hover:bg-rose-50' : 'text-amber-600 border-amber-100 hover:bg-amber-50'} gap-1.5 text-xs font-semibold`}
                onClick={activeTab === 'archived' ? handleDeleteSelected : handleHideSelected}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {activeTab === 'archived' ? 'Delete' : 'Archive'} ({selectedRowKeys.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Modern, Slim Filter Tabs & Compact Search Bar */}
      <div className="bg-white p-3 rounded-xl border border-slate-200/60 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center bg-slate-100 p-0.5 rounded-lg w-full sm:w-fit overflow-x-auto no-scrollbar">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 sm:flex-none px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'active' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Active Queue
            </button>
            <button
              onClick={() => setActiveTab('processed')}
              className={`flex-1 sm:flex-none px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'processed' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Processed
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`flex-1 sm:flex-none px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'archived' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Archived
            </button>
          </div>

          <div className="w-full sm:max-w-xs relative group">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-[#8dc63f] transition-colors" />
            <input
              type="text"
              placeholder="Search history..."
              className="w-full pl-9 pr-3 h-8 bg-slate-50 border border-slate-100 rounded-lg text-xs font-normal focus:bg-white focus:border-[#8dc63f] focus:ring-2 focus:ring-[#8dc63f]/5 transition-all outline-none"
              value={searchVal || ""}
              onChange={(e) => setSearchVal(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Core Table Container with Custom Styling Headers */}
      <div className="request-table-wrapper">
        <Table
          dataSource={filteredData}
          columns={columns.map(col => ({ ...col, className: `col-${col.key}` }))}
          rowKey="_id"
          loading={tableLoading}
          minWidth="100%"
          tableClassName="request-table"
          selection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          onRowClick={(record) => {
            setRequest(record);
            setTargetRequestId(record._id);
            setVisible(true);
          }}
        />
      </div>


      {/* Details Modal Drawer Component */}
      <RequestDetails
        isOpen={visible}
        onClose={() => setVisible(false)}
        onRefresh={handleRefresh}
        deactivateRequest={deactivateRequest}
      />

      {/* Delete/Deactivate Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen || isDeactivateModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setIsDeactivateModalOpen(false); }}
        title={isDeleteModalOpen ? "Delete Request" : "Deactivate Card"}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setIsDeleteModalOpen(false); setIsDeactivateModalOpen(false); }}>Keep It</Button>
            <Button variant="danger" onClick={isDeleteModalOpen ? handleDelete : handleDeactivate}>
              {isDeleteModalOpen ? "Delete Permanently" : "Deactivate Card"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-rose-500" />
          </div>
          <h4 className="text-md font-bold text-slate-900">Are you absolutely sure?</h4>
          <p className="text-xs text-slate-500 mt-2 max-w-xs leading-relaxed">
            {isDeleteModalOpen
              ? "This action cannot be undone. All data related to this request will be permanently removed."
              : "This will immediately revoke access for this card. You'll need a new request to re-enable it."}
          </p>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  requests: state.request.requestData,
  singleRequst: state.request.singleRequst,
  user: state.auth.user,
});

const mapDispatchToProps = {
  getAllRequests,
  setRequest,
  withdrawRequest,
  deleteRequest,
  getCurrentuser,
  deactivateRequest,
  hideRequests,
  claimRequest,
  getAssigneeEmployee,
  getActivators,
  approveRequest,
  rejectRequest,
  assignSupervisor,
  assignStaffs
};

export default connect(mapStateToProps, mapDispatchToProps)(RequestList);